import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Users,
  Plus,
  Search,
  Filter,
  Eye,
  Edit,
  Archive,
  Download,
  UserCheck,
  UserX,
  Briefcase
} from 'lucide-react';
import AdminLayout from '../../../components/admin/AdminLayout';
import { useAdminAuth } from '../../../contexts/AdminAuthContext';

const EmployeesListPage = () => {
  const navigate = useNavigate();
  const { hasPermission } = useAdminAuth();
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterDepartment, setFilterDepartment] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [employees, setEmployees] = useState([]);

  // Mock data
  useEffect(() => {
    setTimeout(() => {
      setEmployees([
        {
          id: 'EMP-001',
          name: 'أحمد محمد العتيبي',
          nationalId: '1234567890',
          position: 'محامي أول',
          department: 'القسم القانوني',
          hireDate: '2020-01-15',
          status: 'active',
          email: 'ahmed.alotaibi@wazin.law',
          phone: '+966501234567',
          contractType: 'غير محدد',
          grade: 'أول',
          salary: 18000
        },
        {
          id: 'EMP-002',
          name: 'فاطمة سعيد الغامدي',
          nationalId: '1234567891',
          position: 'مساعد إداري',
          department: 'القسم الإداري',
          hireDate: '2021-03-20',
          status: 'active',
          email: 'fatimah.alghamdi@wazin.law',
          phone: '+966502345678',
          contractType: 'محدد المدة',
          grade: 'ثاني',
          salary: 12000
        },
        {
          id: 'EMP-003',
          name: 'خالد عبدالله الشمري',
          nationalId: '1234567892',
          position: 'محاسب',
          department: 'القسم المالي',
          hireDate: '2019-06-10',
          status: 'active',
          email: 'khalid.alshammari@wazin.law',
          phone: '+966503456789',
          contractType: 'غير محدد',
          grade: 'أول',
          salary: 15000
        },
        {
          id: 'EMP-004',
          name: 'نورة حسن القحطاني',
          nationalId: '1234567893',
          position: 'محامية',
          department: 'القسم القانوني',
          hireDate: '2022-09-01',
          status: 'trial',
          email: 'noura.alqahtani@wazin.law',
          phone: '+966504567890',
          contractType: 'محدد المدة',
          grade: 'ثالث',
          salary: 10000
        },
        {
          id: 'EMP-005',
          name: 'سعود إبراهيم المطيري',
          nationalId: '1234567894',
          position: 'مدير الموارد البشرية',
          department: 'الموارد البشرية',
          hireDate: '2018-02-15',
          status: 'active',
          email: 'saud.almutairi@wazin.law',
          phone: '+966505678901',
          contractType: 'غير محدد',
          grade: 'مدير',
          salary: 22000
        },
        {
          id: 'EMP-006',
          name: 'منى عبدالرحمن الدوسري',
          nationalId: '1234567895',
          position: 'سكرتيرة تنفيذية',
          department: 'القسم الإداري',
          hireDate: '2023-01-10',
          status: 'suspended',
          email: 'mona.aldosari@wazin.law',
          phone: '+966506789012',
          contractType: 'محدد المدة',
          grade: 'ثاني',
          salary: 8000
        }
      ]);
      setLoading(false);
    }, 500);
  }, []);

  const departments = ['القسم القانوني', 'القسم الإداري', 'القسم المالي', 'الموارد البشرية'];
  const statuses = [
    { value: 'active', label: 'نشط', color: 'green' },
    { value: 'trial', label: 'تحت التجربة', color: 'yellow' },
    { value: 'suspended', label: 'موقوف', color: 'red' },
    { value: 'resigned', label: 'مستقيل', color: 'gray' }
  ];

  // Filtering
  const filteredEmployees = employees.filter((emp) => {
    const matchesSearch = emp.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         emp.nationalId.includes(searchTerm) ||
                         emp.email.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesDept = filterDepartment === 'all' || emp.department === filterDepartment;
    const matchesStatus = filterStatus === 'all' || emp.status === filterStatus;
    return matchesSearch && matchesDept && matchesStatus;
  });

  const getStatusBadge = (status) => {
    const statusObj = statuses.find(s => s.value === status);
    const colors = {
      green: 'bg-green-100 text-green-700 border-green-300',
      yellow: 'bg-yellow-100 text-yellow-700 border-yellow-300',
      red: 'bg-red-100 text-red-700 border-red-300',
      gray: 'bg-gray-100 text-gray-700 border-gray-300'
    };
    return (
      <span className={`px-3 py-1 rounded-full text-xs font-bold border-2 ${colors[statusObj.color]}`}>
        {statusObj.label}
      </span>
    );
  };

  const handleExport = () => {
    alert('تصدير قائمة الموظفين إلى Excel');
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-full">
          <div className="text-center">
            <div className="w-16 h-16 border-4 border-[#1E3A8A] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-gray-600">جاري تحميل الموظفين...</p>
          </div>
        </div>
      </AdminLayout>
    );
  }

  if (!hasPermission('hr.view')) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-full">
          <p className="text-gray-600">ليس لديك صلاحية الوصول لإدارة الموظفين</p>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="p-6 max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-3xl font-black text-[#1E3A8A] mb-2 flex items-center gap-3">
                <Users className="w-8 h-8" />
                <span>إدارة الموظفين</span>
              </h1>
              <p className="text-gray-600">إدارة كاملة للموظفين والملفات الوظيفية</p>
            </div>

            <div className="flex items-center gap-3">
              {hasPermission('hr.export') && (
                <button
                  onClick={handleExport}
                  className="inline-flex items-center gap-2 px-4 py-2 border-2 border-[#C8A870] text-[#C8A870] rounded-lg font-bold hover:bg-[#C8A870] hover:text-white transition-all"
                >
                  <Download className="w-4 h-4" />
                  <span>تصدير</span>
                </button>
              )}
              {hasPermission('hr.add') && (
                <button
                  onClick={() => navigate('/admin/hr/employees/create')}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-[#1E3A8A] text-white rounded-lg font-bold hover:bg-[#152d6e] transition-all"
                >
                  <Plus className="w-5 h-5" />
                  <span>إضافة موظف</span>
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Statistics Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-lg shadow-sm border-2 border-green-200 p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">الموظفون النشطون</p>
                <p className="text-3xl font-black text-green-600">
                  {employees.filter(e => e.status === 'active').length}
                </p>
              </div>
              <UserCheck className="w-12 h-12 text-green-600 opacity-20" />
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm border-2 border-yellow-200 p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">تحت التجربة</p>
                <p className="text-3xl font-black text-yellow-600">
                  {employees.filter(e => e.status === 'trial').length}
                </p>
              </div>
              <Briefcase className="w-12 h-12 text-yellow-600 opacity-20" />
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm border-2 border-[#1E3A8A] p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">إجمالي الموظفين</p>
                <p className="text-3xl font-black text-[#1E3A8A]">{employees.length}</p>
              </div>
              <Users className="w-12 h-12 text-[#1E3A8A] opacity-20" />
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm border-2 border-[#C8A870] p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">الأقسام</p>
                <p className="text-3xl font-black text-[#C8A870]">{departments.length}</p>
              </div>
              <Briefcase className="w-12 h-12 text-[#C8A870] opacity-20" />
            </div>
          </div>
        </div>

        {/* Search and Filters */}
        <div className="bg-white rounded-lg shadow-sm border-2 border-gray-200 p-4 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Search */}
            <div className="relative">
              <Search className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="بحث بالاسم، رقم الهوية، أو البريد..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pr-10 pl-4 py-2 border-2 border-gray-200 rounded-lg focus:border-[#1E3A8A] focus:outline-none"
              />
            </div>

            {/* Department Filter */}
            <div>
              <select
                value={filterDepartment}
                onChange={(e) => setFilterDepartment(e.target.value)}
                className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-[#1E3A8A] focus:outline-none font-semibold"
              >
                <option value="all">جميع الأقسام</option>
                {departments.map((dept) => (
                  <option key={dept} value={dept}>{dept}</option>
                ))}
              </select>
            </div>

            {/* Status Filter */}
            <div>
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-[#1E3A8A] focus:outline-none font-semibold"
              >
                <option value="all">جميع الحالات</option>
                {statuses.map((status) => (
                  <option key={status.value} value={status.value}>{status.label}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Employees Table */}
        <div className="bg-white rounded-lg shadow-sm border-2 border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b-2 border-gray-200">
                <tr>
                  <th className="px-4 py-3 text-right text-sm font-bold text-gray-700">رقم الموظف</th>
                  <th className="px-4 py-3 text-right text-sm font-bold text-gray-700">الاسم</th>
                  <th className="px-4 py-3 text-right text-sm font-bold text-gray-700">المسمى الوظيفي</th>
                  <th className="px-4 py-3 text-right text-sm font-bold text-gray-700">القسم</th>
                  <th className="px-4 py-3 text-right text-sm font-bold text-gray-700">تاريخ التعيين</th>
                  <th className="px-4 py-3 text-center text-sm font-bold text-gray-700">الحالة</th>
                  <th className="px-4 py-3 text-center text-sm font-bold text-gray-700">الإجراءات</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {filteredEmployees.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="px-4 py-8 text-center text-gray-500">
                      <UserX className="w-12 h-12 mx-auto mb-2 text-gray-400" />
                      <p>لا توجد نتائج</p>
                    </td>
                  </tr>
                ) : (
                  filteredEmployees.map((employee) => (
                    <tr key={employee.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-4 py-3 text-sm font-bold text-[#1E3A8A]">{employee.id}</td>
                      <td className="px-4 py-3">
                        <div>
                          <p className="font-bold text-gray-800">{employee.name}</p>
                          <p className="text-xs text-gray-500">{employee.email}</p>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm font-semibold">{employee.position}</td>
                      <td className="px-4 py-3 text-sm">{employee.department}</td>
                      <td className="px-4 py-3 text-sm text-gray-600">{employee.hireDate}</td>
                      <td className="px-4 py-3 text-center">{getStatusBadge(employee.status)}</td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-center gap-2">
                          <button
                            onClick={() => navigate(`/admin/hr/employees/${employee.id}`)}
                            className="p-2 text-[#1E3A8A] hover:bg-blue-50 rounded-lg transition-colors"
                            title="عرض الملف"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                          {hasPermission('hr.edit') && (
                            <button
                              onClick={() => navigate(`/admin/hr/employees/${employee.id}/edit`)}
                              className="p-2 text-[#C8A870] hover:bg-yellow-50 rounded-lg transition-colors"
                              title="تعديل"
                            >
                              <Edit className="w-4 h-4" />
                            </button>
                          )}
                          {hasPermission('hr.archive') && (
                            <button
                              onClick={() => alert(`أرشفة الموظف: ${employee.name}`)}
                              className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                              title="أرشفة"
                            >
                              <Archive className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Results Summary */}
        <div className="mt-4 text-center text-sm text-gray-600">
          عرض {filteredEmployees.length} من أصل {employees.length} موظف
        </div>
      </div>
    </AdminLayout>
  );
};

export default EmployeesListPage;